import { FormControlLabel, Radio } from '@material-ui/core';
import { FormikRadioGroupField, useFormikEnhanced } from '@superdispatch/forms';
import {
  Column,
  Columns,
  Stack,
  VisibilityObserver,
  useSnackbarStack,
} from '@superdispatch/ui';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { FormikDrawerActions } from 'shared/form/FormikDrawer';
import { SlowFormikDrawer } from 'shared/form/SlowFormikDrawer';
import { joinStrings } from 'shared/utils/StringUtils';
import { useTerminalsList } from '../../terminals/data/TerminalsAPI';
import { LoadDTO, LoadUpdateTerminalDTO } from '../data/LoadDTO';
import { useLoadsContext } from '../data/LoadsContext';
import { useMemo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { Box, TextBox } from '@superdispatch/ui-lab';

interface LoadAddTerminalDrawerValues {
  loadGUID: string;
  terminalGUID: string;
}

interface LoadAddTerminalDrawerProps {
  load?: LoadDTO;
  onClose: () => void;
  onSubmitSuccess: (
    response: LoadUpdateTerminalDTO,
    values: LoadAddTerminalDrawerValues,
  ) => void;
}

export function LoadAddTerminalDrawer({
  load,
  onClose,
  onSubmitSuccess,
}: LoadAddTerminalDrawerProps) {
  const isOpen = !!load;
  const {
    data: terminalsList,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useTerminalsList({}, { enabled: isOpen });
  const { loadsAPI } = useLoadsContext();
  const { addSnackbar } = useSnackbarStack();

  const formik = useFormikEnhanced<
    LoadAddTerminalDrawerValues,
    APIResponse<LoadUpdateTerminalDTO>
  >({
    initialValues: { terminalGUID: '', loadGUID: load?.guid || '' },
    onSubmit: ({ loadGUID, terminalGUID }) =>
      loadsAPI.addTerminal(loadGUID, terminalGUID),
    onSubmitSuccess: ({ user_message, data }, values) => {
      onSubmitSuccess(data, values);
      addSnackbar(user_message || 'Terminal added', { variant: 'success' });
    },
    onSubmitFailure: ({ message }) => {
      addSnackbar(message, { variant: 'error' });
    },
  });

  const terminals = useMemo(
    () => terminalsList?.pages.flatMap((page) => page.data) || [],
    [terminalsList],
  );

  return (
    <SlowFormikDrawer
      formik={formik}
      open={isOpen}
      onClose={onClose}
      title="Add Terminal"
      actions={
        !!terminals.length && <FormikDrawerActions submitButtonLabel="Add" />
      }
    >
      {isLoading && <TerminalsSkeleton count={4} />}
      {!isLoading && !terminals.length && (
        <Box marginTop="large">
          <TextBox color="secondary">No terminals found</TextBox>
        </Box>
      )}
      <FormikRadioGroupField name="terminalGUID">
        {terminals.map((option) => {
          const isDisabled = load?.terminals.some(
            (terminal) => terminal.guid === option.guid,
          );
          return (
            <FormControlLabel
              disabled={isDisabled}
              key={option.guid}
              value={option.guid}
              control={<Radio />}
              label={joinStrings(', ', option.name, option.city, option.state)}
            />
          );
        })}
      </FormikRadioGroupField>
      {hasNextPage && (
        <VisibilityObserver
          onChange={(visibility) => {
            if (!isFetchingNextPage && visibility === 'visible') {
              void fetchNextPage();
            }
          }}
          render={({ ref }) => (
            <Box ref={ref} marginTop="xsmall">
              <TerminalsSkeleton count={2} />
            </Box>
          )}
        />
      )}
    </SlowFormikDrawer>
  );
}

function TerminalsSkeleton({ count }: { count: number }) {
  return (
    <Stack>
      {Array.from({ length: count }).map((_, index) => (
        <Columns key={index} space="xsmall" align="center">
          <Column width="content">
            <Skeleton variant="circle" width={20} height={20} />
          </Column>
          <Column width="fluid">
            <Skeleton variant="text" width="100%" height="100%" />
          </Column>
        </Columns>
      ))}
    </Stack>
  );
}
